// ─── src/components/sections/SiteFooter.jsx ──────────────────────────────────
// Live footer driven by footer_config + footer_items in Supabase.
// Falls back gracefully to hardcoded content if DB is unavailable.
// Column IDs: 0=iconic strip, 1=brand(config), 2-6=nav cols, 99=bottom bar.
import { useState, useEffect } from "react";
import { useTheme } from "../../theme/ThemeContext";
import { supabase } from "../../lib/supabaseClient";
import { useBreakpoint } from "../../hooks/useWindowWidth";
import { subscribeNewsletter } from "../../services/newsletterService";
import { imgUrl } from "../../utils/imgUrl";

const GD = "var(--font-heading-primary)";
const NU = "var(--font-body)";
const EDITORIAL_LINE = "The world's finest venues and vendors, carefully selected";

// ── Fallback content (used when Supabase unavailable) ─────────────────────────
const FALLBACK_ICONIC = [
  "The Peninsula Hotels","Raffles","One and Only","Auberge Resorts",
  "The Dorchester","Waldorf Astoria","Conrad","Park Hyatt","Banyan Tree","Jumeirah",
  "Rosewood","Belmond","Aman","Six Senses","Mandarin Oriental",
  "Four Seasons","Fairmont","The Ritz","Villa d'Este","Ashford Castle",
];

const FALLBACK_NAV = [
  {
    title: "Couples",
    links: [
      { label: "Browse Venues",      url: "/venues" },
      { label: "Find Photographers", url: "/vendors/photographers" },
      { label: "Wedding Planners",   url: "/vendors/wedding-planners" },
      { label: "LWD Edit",               url: "/lwd-edit" },
      { label: "Receive The LWD Edit",   url: "/lwd-edit#receive-the-edit" },
      { label: "Publications",       url: "/publications" },
      { label: "Planning Checklist", url: "/planning-checklist" },
    ],
  },
  {
    title: "Vendors",
    links: [
      { label: "Apply for Inclusion", url: "/apply-for-inclusion" },
      { label: "Advertise",          url: "/advertise" },
      { label: "Pricing Plans",      url: "/pricing" },
      { label: "Success Stories",    url: "/success-stories" },
      { label: "Vendor Dashboard",   url: "/vendor-dashboard" },
    ],
  },
  {
    title: "Work With Us",
    links: [
      { label: "Advisory",      url: "/advisory" },
      { label: "Our Approach",  url: "/our-approach" },
      { label: "Fam Trips",     url: "/advisory" },
      { label: "Content Shoots", url: "/advisory" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About Us",            url: "/about" },
      { label: "How Aura Works",      url: "/discovery/aura" },
      { label: "Editorial Standards", url: "/editorial-standards" },
      { label: "Press & Media",       url: "/press" },
      { label: "Careers",             url: "/careers" },
      { label: "Contact",             url: "/contact" },
      { label: "Privacy Policy",      url: "/privacy-policy" },
    ],
  },
];

const FALLBACK_BOTTOM_LINKS = [
  { label: "Privacy", url: "/privacy-policy" },
  { label: "Terms",   url: "/terms-and-conditions" },
  { label: "Cookies", url: "/cookie-policy" },
];

const WA_URL =
  "https://wa.me/447960497211?text=Hello%20Luxury%20Wedding%20Directory%2C%20I%20would%20like%20more%20information";

// ── WhatsApp icon ─────────────────────────────────────────────────────────────
function WhatsAppIcon({ size = 16, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

// ── Social SVG icons ──────────────────────────────────────────────────────────
function SocialSvg({ platform, size = 20, color = "currentColor" }) {
  if (platform === "instagram") return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="17" cy="7" r="1" fill={color} stroke="none" />
    </svg>
  );
  if (platform === "pinterest") return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
      <path d="M12 2C6.48 2 2 6.48 2 12c0 4.24 2.65 7.86 6.39 9.29-.09-.78-.17-1.98.03-2.83.19-.77 1.27-5.39 1.27-5.39s-.32-.65-.32-1.61c0-1.51.88-2.64 1.97-2.64.93 0 1.38.7 1.38 1.54 0 .94-.6 2.34-.91 3.64-.26 1.09.54 1.97 1.6 1.97 1.92 0 3.21-2.47 3.21-5.39 0-2.23-1.51-3.79-3.66-3.79-2.49 0-3.96 1.87-3.96 3.8 0 .75.29 1.56.65 2 .07.09.08.17.06.26-.07.27-.21.87-.24.99-.04.16-.13.19-.3.12-1.12-.52-1.82-2.17-1.82-3.49 0-2.84 2.06-5.44 5.94-5.44 3.12 0 5.54 2.22 5.54 5.19 0 3.1-1.95 5.59-4.66 5.59-.91 0-1.77-.47-2.06-1.03l-.56 2.09c-.2.78-.75 1.75-1.12 2.34.84.26 1.74.4 2.67.4 5.52 0 10-4.48 10-10S17.52 2 12 2z"/>
    </svg>
  );
  if (platform === "tiktok") return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.34 6.34 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34v-7a8.27 8.27 0 0 0 4.84 1.55V6.41a4.85 4.85 0 0 1-1.07-.28z"/>
    </svg>
  );
  return null;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function SiteFooter({
  onNavigateHome,
  onNavigateContact,
  onNavigatePartnership,
  onNavigateAdmin,
  onNavigateAbout,
  onNavigateStandard,
  onViewCategory,
  onNavigateGettingMarried,
  onNavigateArtistryAwards,
  onNavigateMagazine,
  onNavigatePartnerEnquiry,
}) {
  const C = useTheme();
  const { isMobile, isDesktop } = useBreakpoint();
  const [cfg,      setCfg]      = useState(null);
  const [items,    setItems]    = useState(null); // null = still loading
  const [branding, setBranding] = useState(null);

  // Newsletter subscription state
  const [nlEmail,  setNlEmail]  = useState("");
  const [nlStatus, setNlStatus] = useState("idle"); // idle | loading | success | error
  const [nlError,  setNlError]  = useState("");

  useEffect(() => {
    async function load() {
      try {
        const [cfgRes, itemsRes, brandRes] = await Promise.all([
          supabase.from("footer_config").select("*").eq("id", "homepage").maybeSingle(),
          supabase.from("footer_items").select("*").eq("visible", true).order("position"),
          supabase.from("site_branding").select("*").eq("id", "main").maybeSingle(),
        ]);
        if (!cfgRes.error && cfgRes.data)     setCfg(cfgRes.data);
        if (!itemsRes.error && itemsRes.data)  setItems(itemsRes.data);
        if (!brandRes.error && brandRes.data)  setBranding(brandRes.data);
      } catch {
        // Supabase unavailable - keep null, render fallback below
      }
    }
    load();
  }, []);

  // ── Derived values ──────────────────────────────────────────────────────────
  const gold      = cfg?.accent_color  || C.gold || "#d39a21";
  const textColor = cfg?.text_color    || "#d4c8b0";
  const bgColor   = cfg?.bg_color      || "#0a0a08";
  const bbBg      = cfg?.bottom_bar_bg || "#080604";
  const bbText    = cfg?.bottom_bar_text || "rgba(255,255,255,0.55)";
  const padX      = isMobile ? 20 : (cfg?.pad_x ?? 64);

  // Group visible items by column_id, sorted by position
  const grouped = {};
  (items || []).forEach(item => {
    if (!grouped[item.column_id]) grouped[item.column_id] = [];
    grouped[item.column_id].push(item);
  });

  // Iconic strip names
  const iconicBlock = (grouped[0] || []).find(i => i.block_type === "iconic_venues");
  const iconicNames = iconicBlock?.iconic_venues?.length
    ? iconicBlock.iconic_venues.map(e => e.name).filter(Boolean)
    : FALLBACK_ICONIC;

  // Repeat for seamless marquee (min 3x to fill any viewport)
  const reps = Math.max(3, Math.ceil(30 / iconicNames.length));
  const marqueeNames = Array.from({ length: reps }, () => iconicNames).flat();

  // Nav columns (2-6): build from DB when loaded, else use fallback
  let navCols;
  if (items !== null) {
    navCols = [2, 3, 4, 5, 6]
      .map(colId => {
        const colItems = grouped[colId] || [];
        const heading  = colItems.find(i => i.block_type === "heading");
        const links    = colItems.filter(i => i.block_type === "link");
        return heading ? { colId, title: heading.content || heading.label, links } : null;
      })
      .filter(Boolean);
    // Fall back to FALLBACK_NAV if DB returned nothing for nav cols
    if (!navCols.length) navCols = FALLBACK_NAV.map((c, i) => ({ colId: i + 2, ...c }));
  } else {
    navCols = FALLBACK_NAV.map((c, i) => ({ colId: i + 2, ...c }));
  }

  // Guarantee Editorial Standards appears in whichever column is titled "Company"
  // (or the last column if none is labelled Company). This ensures the link is
  // always present regardless of what the DB footer_items table contains.
  const GUARANTEED_EDITORIAL = { id: "_es_guaranteed", label: "Editorial Standards", url: "/editorial-standards" };
  const companyCol = navCols.find(c => /company/i.test(c.title));
  if (companyCol) {
    const alreadyLinked = companyCol.links.some(l => (l.url || "").includes("editorial-standards"));
    if (!alreadyLinked) companyCol.links.push(GUARANTEED_EDITORIAL);
  }

  // Couples sign-in: guaranteed at the top of the Couples column, own line
  // beneath a thin divider, regardless of what footer_items holds.
  const COUPLES_LOGIN = { id: "_couples_login_guaranteed", label: "Couple Sign In", url: "/getting-married/login", dividerAfter: true };
  const couplesCol = navCols.find(c => /couples/i.test(c.title));
  if (couplesCol) {
    const alreadyLinked = couplesCol.links.some(l => (l.url || "").includes("/login"));
    if (!alreadyLinked) couplesCol.links.unshift(COUPLES_LOGIN);
  }

  // Vendor sign-in: same guaranteed top-of-column + divider treatment.
  const VENDOR_LOGIN = { id: "_vendor_login_guaranteed", label: "Vendor Sign In", url: "/vendor/login", dividerAfter: true };
  const vendorsCol = navCols.find(c => /vendors/i.test(c.title));
  if (vendorsCol) {
    const alreadyLinked = vendorsCol.links.some(l => (l.url || "").includes("/login"));
    if (!alreadyLinked) vendorsCol.links.unshift(VENDOR_LOGIN);
  }

  // Bottom bar links (col 99)
  const HIDDEN_BOTTOM = new Set(["sitemap", "admin"]);
  const bottomLinks = ((grouped[99] || []).length ? grouped[99] : FALLBACK_BOTTOM_LINKS)
    .filter(item => !HIDDEN_BOTTOM.has((item.label || "").toLowerCase()));

  // Social (only real URLs)
  const socials = [];
  if (cfg?.social_instagram) socials.push({ platform: "instagram", url: cfg.social_instagram, label: "Instagram" });
  if (cfg?.social_pinterest) socials.push({ platform: "pinterest", url: cfg.social_pinterest, label: "Pinterest" });
  if (cfg?.social_tiktok)    socials.push({ platform: "tiktok",    url: cfg.social_tiktok,    label: "TikTok" });

  // Strip label
  const stripLabel    = cfg?.strip_label || "Iconic Venues";
  const showNewsletter = cfg ? cfg.show_newsletter : false;
  const showBottomBar   = cfg ? cfg.show_bottom_bar : true;
  const copyrightText   = cfg?.copyright_text || `${new Date().getFullYear()} Luxury Wedding Directory · All rights reserved`;
  const showTaigenic    = cfg ? cfg.show_taigenic !== false : true;
  const taigenicLabel   = cfg?.taigenic_label   || "Powered by Taigenic.AI";
  const taigenicTagline = cfg?.taigenic_tagline || "AI systems for luxury brands";
  const taigenicUrl     = cfg?.taigenic_url     || "/taigenic";
  const taigenicSymbol  = cfg?.taigenic_symbol  || "✦";

  // Grid columns: brand (wider) + one per nav col
  const gridTemplateColumns = `2fr ${navCols.map(() => "1fr").join(" ")}`;

  // SPA-safe link navigation, internal paths use pushState, external use href
  function spaNavigate(url) {
    if (!url || url === "#") return;
    if (url.startsWith("/")) {
      window.history.pushState({}, "", url);
      window.dispatchEvent(new PopStateEvent("popstate"));
      window.scrollTo({ top: 0, behavior: "instant" });
    } else {
      window.location.href = url;
    }
  }

  // Link click handler: URL-first, then callback fallback
  function handleLinkClick(item) {
    if (item.url && item.url !== "#") {
      spaNavigate(item.url);
      return;
    }
  }

  async function handleNlSubmit(e) {
    e.preventDefault();
    const trimmed = nlEmail.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setNlError("Please enter a valid email address.");
      return;
    }
    setNlStatus("loading");
    setNlError("");
    const result = await subscribeNewsletter({ email: trimmed, source: "footer" });
    if (result.ok) {
      setNlStatus("success");
      setNlEmail("");
    } else {
      setNlStatus("error");
      setNlError(result.error || "Something went wrong. Please try again.");
    }
  }

  const linkStyle = (hovered) => ({
    fontFamily: NU,
    fontSize: 12,
    color: hovered ? gold : "rgba(255,255,255,0.65)",
    marginBottom: 9,
    cursor: "pointer",
    transition: "color 0.2s",
    textDecoration: "none",
    display: "block",
  });

  return (
    <footer
      className="site-footer"
      aria-label="Site footer"
      style={{
        background: bgColor,
        borderTop: `1px solid rgba(211,154,33,0.12)`,
        // Aura-launcher clearance used to live here as the footer's own padding-bottom,
        // which painted 80px of the LIGHTER footer colour beneath the dark bottom bar —
        // reading as a stray block at the base of the page. The clearance still exists,
        // it is just carried by the trailing spacer below, which uses the bottom bar's
        // colour so the dark band now runs to the very edge.
        paddingBottom: 0,
      }}
    >
      {/* ── Editorial line ──────────────────────────────────────────────── */}
      <div className="site-footer-tagline" style={{
        textAlign: "center",
        padding: "52px 0 36px",
        background: bgColor,
      }}>
        <p style={{
          fontFamily: GD,
          fontSize: 20,
          fontWeight: 400,
          fontStyle: "italic",
          color: textColor,
          opacity: 0.82,
          letterSpacing: "0.02em",
          lineHeight: 1.5,
          margin: 0,
        }}>
          {EDITORIAL_LINE}
        </p>
      </div>

      {/* ── Separator before strip ──────────────────────────────────────── */}
      <div aria-hidden="true" className="site-footer-separator" style={{
        height: 1,
        background: `linear-gradient(90deg, transparent, ${gold}, rgba(116,129,114,0.6), transparent)`,
        margin: `0 ${padX}px 0`,
      }} />

      {/* ── Iconic Venues marquee strip ─────────────────────────────────── */}
      <div style={{ maxWidth: "100%", margin: "0 auto", padding: `${cfg?.strip_pad_y ?? 20}px 0` }}>
        {/* Label */}
        <div style={{ textAlign: "center", marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16 }}>
            <div style={{ width: 48, height: 1, background: gold, opacity: 0.45 }} />
            <span style={{
              fontFamily: NU, fontSize: 8, fontWeight: 600,
              letterSpacing: "0.28em", textTransform: "uppercase",
              color: gold, opacity: 0.85,
            }}>{stripLabel}</span>
            <div style={{ width: 48, height: 1, background: gold, opacity: 0.45 }} />
          </div>
        </div>

        {/* Scroll band */}
        <div
          className="site-footer-scroll-band"
          style={{
            overflow: "hidden",
            maskImage: "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)",
            WebkitMaskImage: "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)",
          }}
        >
          <div
            className="site-footer-scroll-track"
            style={{ display: "flex", alignItems: "center", whiteSpace: "nowrap", width: "max-content" }}
          >
            {marqueeNames.map((name, i) => (
              <span key={i} style={{ display: "inline-flex", alignItems: "center" }}>
                <span style={{
                  fontFamily: NU, fontSize: 11, fontWeight: 400,
                  letterSpacing: "0.15em", textTransform: "uppercase",
                  color: "rgba(255,255,255,0.55)",
                }}>{name}</span>
                <span aria-hidden="true" style={{
                  color: gold, fontSize: 6, opacity: 0.4, margin: "0 40px",
                }}>·</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Main footer grid ────────────────────────────────────────────── */}
      <div className="site-footer-body" style={{ maxWidth: "100%", padding: `0 ${padX}px` }}>
        <div aria-hidden="true" style={{
          height: 1, background: "rgba(255,255,255,0.06)", marginBottom: 52,
        }} />

        <div
          className="site-footer-grid"
          style={{
            display: "grid",
            gridTemplateColumns: !isDesktop ? "1fr 1fr" : gridTemplateColumns,
            gap: !isDesktop ? "36px 24px" : 48,
            marginBottom: 52,
            alignItems: "start",
          }}
        >
          {/* ── Brand column ── */}
          <div
            className="site-footer-brand"
            style={{ paddingTop: 0, marginTop: 0, gridColumn: !isDesktop ? "1 / -1" : "auto" }}
          >
            {(() => {
              // Resolve logo settings: prefer site_branding, fall back to footer_config legacy fields
              const showLogo = branding
                ? branding.show_logo_in_footer !== false
                : (cfg?.show_logo ?? true);

              if (!showLogo) return null;

              // Determine which logo to render (footer override or inherited)
              let logoType, logoText, logoImageUrl, logoHeight, logoFont, logoColor;
              if (branding) {
                const sameLogo = branding.use_same_logo_everywhere !== false;
                const footerImgOverride = !sameLogo && branding.logo_image_footer;
                // Locked to "text" (was briefly "image" 2026-09-13 when the header's
                // logo_type flip landed, since header and footer shared branding.logo_type
                // — Tai wants the footer to keep the original "LWD" text lockup regardless
                // of what the header shows, so this no longer reads branding.logo_type at
                // all). If a genuine footer image override is ever wanted again, reintroduce
                // it explicitly here rather than inheriting the header's type.
                logoType     = "text";
                logoText     = branding.logo_text || "Luxury Wedding Directory";
                logoImageUrl = footerImgOverride
                  ? branding.logo_image_footer
                  // Footer background defaults to near-black (#0a0a08, see bgColor below),
                  // so the dark-bg (white) variant is correct here, not logo_image_light —
                  // same bug class as the header's useLight mismatch, fixed 2026-09-11.
                  : (branding.logo_image_dark || branding.logo_image_light || null);
                logoHeight   = 80;
                logoFont     = branding.logo_font   || "serif";
                logoColor    = branding.logo_color  || null;
              } else {
                // Legacy footer_config fallback
                logoType     = cfg?.logo_type === "image" ? "image" : "text";
                logoText     = cfg?.logo_text  || "Luxury Wedding Directory";
                logoImageUrl = cfg?.logo_url;
                logoHeight   = cfg?.logo_size  || 48;
                logoFont     = "serif";
                logoColor    = null;
              }

              if (logoType === "image" && logoImageUrl) {
                // Explicit height alongside width in the transform request — resize:'contain'
                // needs both to scale proportionally, or it leaves height at the source's
                // native pixels and squishes a wide lockup into a near-square box (same bug
                // class documented in imgUrl.js, fixed here 2026-09-13).
                const transformW = Math.round(logoHeight * (1830 / 488));
                return (
                  <img
                    src={imgUrl(logoImageUrl, { width: transformW, height: logoHeight, resize: 'contain' })}
                    alt={logoText}
                    style={{
                      height: logoHeight,
                      maxWidth: "100%",
                      objectFit: "contain",
                      display: "block",
                      marginBottom: 6,
                    }}
                  />
                );
              }

              const fontFamily = logoFont === "sans" ? NU : GD;
              const words = logoText.split(" ");
              return (
                <div style={{ textAlign: !isDesktop ? "center" : "left" }}>
                  {!isDesktop ? (
                    // Mobile: stacked word-per-line, elegant
                    <div style={{
                      fontFamily, color: logoColor || gold,
                      letterSpacing: "0.28em", textTransform: "uppercase",
                      lineHeight: 1.3, marginBottom: 6,
                    }}>
                      {words.map((w, i) => (
                        <div key={i} style={{ fontSize: i === 0 ? 11 : i === words.length - 1 ? 20 : 13 }}>
                          {w}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      fontFamily,
                      fontSize: branding ? 22 : (cfg?.logo_size || 10),
                      color: logoColor || gold,
                      letterSpacing: "0.22em",
                      textTransform: "uppercase",
                      lineHeight: 1.15,
                      marginBottom: 6,
                    }}>
                      {words.length > 1
                        ? <>{words.slice(0, -1).join(" ")}<br />{words[words.length - 1]}</>
                        : words[0]
                      }
                    </div>
                  )}
                </div>
              );
            })()}
            <div style={{
              fontFamily: NU, fontSize: 9, letterSpacing: "3px",
              textTransform: "uppercase", color: "rgba(255,255,255,0.58)", marginBottom: 20,
              textAlign: !isDesktop ? "center" : "left",
            }}>
              {cfg?.brand_est_text || "Est. 2006 · Worldwide"}
            </div>

            {/* Gold divider */}
            <div aria-hidden="true" style={{
              width: 40, height: 1,
              background: !isDesktop
                ? `linear-gradient(90deg, transparent, ${gold}, transparent)`
                : `linear-gradient(90deg, ${gold}, transparent)`,
              marginBottom: 18,
              marginLeft: !isDesktop ? "auto" : 0,
              marginRight: !isDesktop ? "auto" : 0,
            }} />

            {(cfg?.show_tagline ?? true) && isDesktop && (
              <p style={{
                fontFamily: NU, fontSize: 13,
                color: "rgba(255,255,255,0.62)",
                lineHeight: 1.85, maxWidth: 300,
                margin: 0, marginBottom: 18,
              }}>
                {cfg?.tagline_text || "The world's most trusted luxury wedding directory. Connecting discerning couples with exceptional venues and professionals across 62 countries."}
              </p>
            )}

            {/* Office, desktop only */}
            {isDesktop && (
              <div style={{ marginBottom: 22 }}>
                <div style={{
                  fontFamily: NU, fontSize: 10, letterSpacing: "0.2em",
                  textTransform: "uppercase", color: "rgba(255,255,255,0.62)", marginBottom: 4,
                }}>Office</div>
                <div style={{
                  fontFamily: NU, fontSize: 12,
                  color: "rgba(255,255,255,0.75)", lineHeight: 1.6,
                }}>{cfg?.brand_office_text || "Worldwide · London Headquarters"}</div>
              </div>
            )}

            {/* Social links (only real URLs) */}
            {(cfg?.show_social ?? true) && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: !isDesktop ? "center" : "flex-start" }}>
                {socials.map(({ platform, url, label }) => (
                  <a
                    key={platform}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={label}
                    style={{
                      width: 36, height: 36,
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      borderRadius: "var(--lwd-radius-input)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "rgba(255,255,255,0.35)",
                      cursor: "pointer", transition: "all 0.2s", textDecoration: "none",
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.borderColor = gold;
                      e.currentTarget.style.color = gold;
                      e.currentTarget.style.background = "rgba(211,154,33,0.08)";
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
                      e.currentTarget.style.color = "rgba(255,255,255,0.35)";
                      e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                    }}
                  >
                    <SocialSvg platform={platform} size={18} color="currentColor" />
                  </a>
                ))}

                {/* WhatsApp always shown */}
                <a
                  href={WA_URL}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Chat with us on WhatsApp"
                  style={{
                    width: 36, height: 36,
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: "var(--lwd-radius-input)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "rgba(255,255,255,0.35)",
                    cursor: "pointer", transition: "all 0.2s", textDecoration: "none",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = gold;
                    e.currentTarget.style.color = gold;
                    e.currentTarget.style.background = "rgba(211,154,33,0.08)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
                    e.currentTarget.style.color = "rgba(255,255,255,0.35)";
                    e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                  }}
                >
                  <WhatsAppIcon size={16} color="currentColor" />
                </a>
              </div>
            )}
          </div>

          {/* ── Nav columns (DB-driven) ── */}
          {navCols.map(({ colId, title, links }) => (
            <div key={colId}>
              <div style={{
                fontFamily: NU,
                fontSize: isMobile ? 11 : 9,
                letterSpacing: isMobile ? "0.20em" : "0.25em",
                textTransform: "uppercase", color: gold,
                opacity: 0.9, marginBottom: isMobile ? 20 : 18, fontWeight: 600,
              }}>
                {title}
              </div>
              {links.map(item => {
                const label = item.label || item.text || "";
                const href  = item.url && item.url !== "" ? item.url : null;
                const mobileLink = isMobile
                  ? { ...linkStyle(false), fontSize: 14, marginBottom: 14 }
                  : linkStyle(false);
                const node = href ? (
                  <a
                    key={item.id || label}
                    href={href}
                    style={mobileLink}
                    onMouseEnter={e => (e.currentTarget.style.color = gold)}
                    onMouseLeave={e => (e.currentTarget.style.color = "rgba(255,255,255,0.65)")}
                  >
                    {label}
                  </a>
                ) : (
                  <div
                    key={item.id || label}
                    role="link"
                    tabIndex={0}
                    style={mobileLink}
                    onMouseEnter={e => (e.currentTarget.style.color = gold)}
                    onMouseLeave={e => (e.currentTarget.style.color = "rgba(255,255,255,0.65)")}
                    onClick={() => handleLinkClick(item)}
                    onKeyDown={e => e.key === "Enter" && handleLinkClick(item)}
                  >
                    {label}
                  </div>
                );
                if (!item.dividerAfter) return node;
                return (
                  <div key={`${item.id || label}-wrap`}>
                    {node}
                    <div style={{
                      height: 1,
                      background: "rgba(255,255,255,0.1)",
                      margin: isMobile ? "10px 0 14px" : "8px 0 12px",
                    }} />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* ── Newsletter strip ────────────────────────────────────────────── */}
      {showNewsletter && (
        <div style={{
          background: cfg?.newsletter_bg || "#000000",
          borderTop: `1px solid ${cfg?.newsletter_border_color || "#2d2d2d"}`,
          borderBottom: `1px solid ${cfg?.newsletter_border_color || "#2d2d2d"}`,
          position: "relative",
          overflow: "hidden",
        }}>

          {/* Content, responsive */}
          <div className="site-footer-newsletter" style={{
            padding: `${cfg?.newsletter_pad_y ?? 20}px ${padX}px`,
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "flex-start" : "center",
            justifyContent: "space-between",
            gap: isMobile ? 24 : 40,
            position: "relative",
          }}>
            {/* Editorial copy */}
            <div>
              <div style={{
                fontFamily: NU, fontSize: 8, fontWeight: 700,
                letterSpacing: "0.22em", textTransform: "uppercase",
                color: gold, opacity: 0.9, marginBottom: 8,
              }}>{cfg?.newsletter_label || "The Editorial"}</div>
              <div style={{
                fontFamily: GD, fontSize: isMobile ? 20 : 24,
                color: textColor, marginBottom: 6, lineHeight: 1.2,
              }}>
                {cfg?.newsletter_heading || "The LWD Edit, monthly inspiration from Luxury Wedding Directory"}
              </div>
              <div style={{
                fontFamily: NU, fontSize: 13,
                color: textColor, opacity: 0.72, lineHeight: 1.6,
                maxWidth: 420,
              }}>
                {cfg?.newsletter_subtext || "Extraordinary venues, real weddings, and planning guides. No spam."}
              </div>
            </div>

            {/* Subscribe form */}
            {nlStatus === "success" ? (
              <div style={{
                padding: "14px 20px",
                border: `1px solid ${gold}44`,
                borderRadius: 3,
                background: `${gold}0d`,
                fontFamily: NU, fontSize: 13,
                color: gold, letterSpacing: "0.04em",
                flexShrink: 0,
              }}>
                ✦ &nbsp;You're on the list.
              </div>
            ) : (
              <form
                onSubmit={handleNlSubmit}
                className="site-footer-subscribe"
                style={{
                  display: "flex", flexDirection: "column",
                  flexShrink: 0,
                  width: isMobile ? "100%" : 380,
                  gap: 0,
                }}
              >
                <div style={{ display: "flex", alignItems: "stretch" }}>
                  <input
                    type="email"
                    value={nlEmail}
                    onChange={e => { setNlEmail(e.target.value); setNlError(""); }}
                    placeholder="Your email address"
                    aria-label="Email address for newsletter"
                    disabled={nlStatus === "loading"}
                    style={{
                      flex: 1,
                      background: "rgba(255,255,255,0.04)",
                      borderTop: `1px solid ${nlError ? "#c0392b" : gold}45`,
                      borderBottom: `1px solid ${nlError ? "#c0392b" : gold}45`,
                      borderLeft: `1px solid ${nlError ? "#c0392b" : gold}45`,
                      borderRight: "none",
                      color: textColor, fontFamily: NU, fontSize: 13,
                      padding: "13px 16px", outline: "none",
                      letterSpacing: "0.01em",
                    }}
                  />
                  <button
                    type="submit"
                    disabled={nlStatus === "loading"}
                    style={{
                      background: nlStatus === "loading"
                        ? "rgba(211,154,33,0.4)"
                        : `linear-gradient(135deg, ${gold} 0%, #9a7832 100%)`,
                      border: "none",
                      color: "#0a0800", fontFamily: NU, fontSize: 10,
                      fontWeight: 700, letterSpacing: "0.14em",
                      textTransform: "uppercase", padding: "13px 22px",
                      cursor: nlStatus === "loading" ? "wait" : "pointer",
                      flexShrink: 0, whiteSpace: "nowrap",
                    }}
                  >
                    {nlStatus === "loading" ? "…" : (cfg?.newsletter_btn_label || "Subscribe")}
                  </button>
                </div>
                {nlError && (
                  <div style={{ fontFamily: NU, fontSize: 11, color: "#e07070", marginTop: 6 }}>
                    {nlError}
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      )}

      {/* ── Bottom bar ─────────────────────────────────────────────────── */}
      {showBottomBar && (
        <div
          className="site-footer-legal"
          style={{
            background: bbBg,
            padding: `14px ${padX}px 8px`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
            textAlign: "center",
          }}
        >
          {/* Copyright */}
          <div>
            <div style={{ fontFamily: NU, fontSize: 11, color: bbText }}>
              &copy; {copyrightText}
            </div>
            <div style={{ fontFamily: NU, fontSize: 10, color: "rgba(255,255,255,0.65)", marginTop: 3 }}>
              <a
                href="https://5starweddingdirectory.com"
                target="_blank"
                rel="noreferrer"
                style={{ color: "inherit", textDecoration: "none" }}
                onMouseEnter={e => e.currentTarget.style.color = gold}
                onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.65)"}
              >A 5 Star Weddings Company</a> · Est. 2006
            </div>
          </div>

          {/* Policy links, centred */}
          <div className="site-footer-legal-links" style={{ display: "flex", gap: 20, flexWrap: "wrap", justifyContent: "center" }}>
            {bottomLinks.map(item => {
              const label = item.label || item.text || "";
              return (
                <span
                  key={item.id || label}
                  role="link"
                  tabIndex={0}
                  onClick={() => {
                    if (label === "Admin")   { onNavigateAdmin?.(); return; }
                    if (label === "Cookies") { window.dispatchEvent(new Event("lwd:show-cookies")); return; }
                    if (item.url && item.url !== "") spaNavigate(item.url);
                  }}
                  onKeyDown={e => {
                    if (e.key === "Enter" && label === "Admin") onNavigateAdmin?.();
                  }}
                  style={{
                    fontFamily: NU, fontSize: 11,
                    color: bbText, cursor: "pointer", transition: "color 0.2s",
                  }}
                  onMouseEnter={e => (e.currentTarget.style.color = gold)}
                  onMouseLeave={e => (e.currentTarget.style.color = bbText)}
                >
                  {label}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Taigenic signature ───────────────────────────────────────────── */}
      {showBottomBar && showTaigenic && (
        <div style={{ background: bbBg, padding: "6px 0 20px", textAlign: "center" }}>
          <a
            href={taigenicUrl}
            style={{
              textDecoration: "none",
              display: "inline-block",
              padding: "8px 24px",
              borderRadius: 6,
              transition: "background 0.3s",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = "rgba(211,154,33,0.06)";
              e.currentTarget.querySelector(".tai-line1").style.color = gold;
              e.currentTarget.querySelector(".tai-line2").style.opacity = "0.65";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.querySelector(".tai-line1").style.color = "rgba(255,255,255,0.48)";
              e.currentTarget.querySelector(".tai-line2").style.opacity = "1";
            }}
          >
            <span className="tai-line1" style={{
              display: "block",
              fontFamily: NU,
              fontSize: 11,
              fontVariant: "small-caps",
              letterSpacing: "2.5px",
              color: "rgba(255,255,255,0.48)",
              transition: "color 0.3s",
            }}>
              {taigenicSymbol} {taigenicLabel}
            </span>
            <span className="tai-line2" style={{
              display: "block",
              fontFamily: NU,
              fontSize: 9,
              letterSpacing: "1.5px",
              color: "rgba(255,255,255,0.50)",
              marginTop: 2,
              transition: "opacity 0.3s",
            }}>
              {taigenicTagline}
            </span>
          </a>
        </div>
      )}

      {/* ── Aura-launcher clearance ──────────────────────────────────────────
          Reserves the same space the footer's padding-bottom used to, but paints
          it in the bottom bar's colour so the dark band reaches the page edge.
          --fui-compare-height (set by FloatingUiManager) adds room when the
          compare bar is open and pushes the Aura launcher upward.
          bbBg when a bottom bar is present, otherwise the footer's own colour —
          a bare spacer must never introduce a second tone of its own. */}
      <div
        aria-hidden="true"
        style={{
          background: showBottomBar ? bbBg : bgColor,
          height: "calc(80px + var(--fui-compare-height, 0px))",
        }}
      />
    </footer>
  );
}
